import { useState, useEffect, forwardRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Briefcase, GraduationCap, Sparkles, Mail, Phone, MapPin, Linkedin, Github, Globe, DollarSign, Calendar, FolderTree, FolderKanban, Award, BadgeCheck, Brain } from 'lucide-react';
import DOMPurify from 'dompurify';

interface PrintCandidateProfileProps {
  candidate: any;
  isAnonymized: boolean;
  showLogo: boolean;
  showAISummary?: boolean;
}

interface SkillBucket {
  id: string;
  name: string;
  skills: { id: string; name: string }[];
}

const PrintCandidateProfile = forwardRef<HTMLDivElement, PrintCandidateProfileProps>(
  ({ candidate, isAnonymized, showLogo, showAISummary = true }, ref) => {
    const [logoUrl, setLogoUrl] = useState<string | null>(null);
    const [skillBuckets, setSkillBuckets] = useState<SkillBucket[]>([]);

    useEffect(() => {
      if (showLogo) {
        fetchLogo();
      }
    }, [showLogo]);

    useEffect(() => {
      if (candidate.candidate_skills?.length > 0) {
        organizeSkillsByBucket();
      }
    }, [candidate.candidate_skills]);

    const fetchLogo = async () => {
      try {
        const { data } = await supabase
          .from('app_settings')
          .select('setting_value')
          .eq('setting_key', 'company_logo')
          .maybeSingle();

        if (data?.setting_value) {
          const { data: urlData } = supabase.storage
            .from('company-assets')
            .getPublicUrl(data.setting_value);
          setLogoUrl(urlData.publicUrl);
        }
      } catch (error) {
        console.error('Error fetching logo:', error);
      }
    };

    const organizeSkillsByBucket = async () => {
      try {
        const { data: allSkills } = await supabase
          .from('skills')
          .select('id, name, parent_id, is_bucket');

        if (!allSkills) return;

        const skillsMap = new Map(allSkills.map(s => [s.id, s]));
        const bucketsMap = new Map<string, { id: string; name: string; skills: { id: string; name: string }[] }>();

        const findNearestBucket = (skillId: string): { id: string; name: string } | null => {
          let current = skillsMap.get(skillId);
          const visited = new Set<string>();

          while (current) {
            if (visited.has(current.id)) break;
            visited.add(current.id);

            if (current.is_bucket) {
              return { id: current.id, name: current.name };
            }
            if (current.parent_id) {
              current = skillsMap.get(current.parent_id);
            } else {
              break;
            }
          }
          return null;
        };

        const skillsByName = new Map<string, any>();
        allSkills.forEach(s => {
          skillsByName.set(s.name.toLowerCase(), s);
        });

        for (const cs of candidate.candidate_skills || []) {
          const skill = cs.skills;
          if (!skill) continue;

          let bucket: { id: string; name: string } | null = null;

          const candidateSkillRecord = skillsMap.get(skill.id);
          if (candidateSkillRecord?.parent_id) {
            bucket = findNearestBucket(candidateSkillRecord.parent_id);
          }

          if (!bucket) {
            const taxonomySkill = skillsByName.get(skill.name.toLowerCase());
            if (taxonomySkill && taxonomySkill.parent_id) {
              bucket = findNearestBucket(taxonomySkill.parent_id);
            }
          }

          const bucketKey = bucket?.id || 'uncategorized';
          const bucketName = bucket?.name || 'Other Skills';

          if (!bucketsMap.has(bucketKey)) {
            bucketsMap.set(bucketKey, { id: bucketKey, name: bucketName, skills: [] });
          }
          bucketsMap.get(bucketKey)!.skills.push({ id: skill.id, name: skill.name });
        }

        const sortedBuckets = Array.from(bucketsMap.values()).sort((a, b) => {
          if (a.id === 'uncategorized') return 1;
          if (b.id === 'uncategorized') return -1;
          return a.name.localeCompare(b.name);
        });

        setSkillBuckets(sortedBuckets);
      } catch (error) {
        console.error('Error organizing skills by bucket:', error);
      }
    };

    const displayName = isAnonymized
      ? `Candidate ${candidate.id.slice(0, 8).toUpperCase()}`
      : `${candidate.first_name} ${candidate.last_name}`;

    const formatSalary = () => {
      if (!candidate.desired_salary_min && !candidate.desired_salary_max) return null;
      const currency = candidate.desired_salary_currency || 'USD';
      if (candidate.desired_salary_min && candidate.desired_salary_max) {
        return `${currency} ${candidate.desired_salary_min.toLocaleString()} - ${candidate.desired_salary_max.toLocaleString()}`;
      }
      if (candidate.desired_salary_min) {
        return `${currency} ${candidate.desired_salary_min.toLocaleString()}+`;
      }
      return `Up to ${currency} ${candidate.desired_salary_max.toLocaleString()}`;
    };

    const formatDate = (dateStr: string | null) => {
      if (!dateStr) return 'N/A';
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    };

    // Get certifications from skills
    const certifications = candidate.candidate_skills?.filter(
      (cs: any) => cs.skills?.type === 'certification'
    ) || [];

    // Get awards
    const awards = candidate.candidate_awards || [];

    // Render rich text content safely with proper styling
    const renderRichText = (content: string | null) => {
      if (!content) return null;
      
      // Check if it's HTML content (has actual HTML tags, not just < > in text)
      if (/<[a-z][\s\S]*>/i.test(content)) {
        const sanitized = DOMPurify.sanitize(content, {
          ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 's', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'span'],
          ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style']
        });
        return (
          <div 
            className="rich-text-print"
            style={{ 
              fontSize: '9pt',
              lineHeight: '1.5'
            }}
            dangerouslySetInnerHTML={{ __html: sanitized }} 
          />
        );
      }
      
      // Plain text - convert bullets and formatting (support *, -, •)
      const lines = content.split('\n');
      const bulletPattern = /^[\s]*[*\-•]\s+/;
      const hasBullets = lines.some(line => bulletPattern.test(line));
      
      if (hasBullets) {
        const bulletItems = lines
          .filter(line => line.trim())
          .map(line => line.replace(/^[\s]*[*\-•]\s+/, '').trim());
        
        return (
          <ul style={{ 
            margin: '4px 0', 
            paddingLeft: '18px', 
            fontSize: '9pt',
            listStyleType: 'disc'
          }}>
            {bulletItems.map((item, i) => (
              <li key={i} style={{ marginBottom: '3px', lineHeight: '1.4' }}>
                {item}
              </li>
            ))}
          </ul>
        );
      }
      
      // Regular text - split paragraphs
      const paragraphs = content.split('\n\n').filter(p => p.trim());
      if (paragraphs.length > 1) {
        return (
          <div style={{ fontSize: '9pt' }}>
            {paragraphs.map((para, i) => (
              <p key={i} style={{ margin: '0 0 4px 0', lineHeight: '1.4' }}>
                {para.replace(/\n/g, ' ')}
              </p>
            ))}
          </div>
        );
      }
      
      return <p style={{ margin: 0, fontSize: '9pt', lineHeight: '1.4' }}>{content}</p>;
    };

    return (
      <div 
        ref={ref} 
        className="bg-white text-black print:text-black"
        style={{
          fontFamily: 'Arial, Helvetica, sans-serif',
          fontSize: '10pt',
          lineHeight: '1.4',
          padding: '0.5in',
          maxWidth: '8.5in',
          margin: '0 auto',
        }}
      >
        {/* Print-specific styles */}
        <style>{`
          @media print {
            @page {
              margin: 0.5in;
              size: letter;
            }
            .print-section {
              page-break-inside: avoid;
            }
            .page-break-before {
              page-break-before: always;
            }
            .avoid-break {
              break-inside: avoid;
            }
          }
          .rich-text-print { font-size: 9pt; line-height: 1.5; }
          .rich-text-print p { margin: 0 0 6px 0 !important; }
          .rich-text-print ul, .rich-text-print ol { 
            margin: 6px 0 !important; 
            padding-left: 18px !important; 
            list-style-type: disc !important;
          }
          .rich-text-print ol { list-style-type: decimal !important; }
          .rich-text-print li { 
            margin-bottom: 3px !important; 
            display: list-item !important;
          }
          .rich-text-print strong, .rich-text-print b { font-weight: 600; }
          .rich-text-print em, .rich-text-print i { font-style: italic; }
          .rich-text-print u { text-decoration: underline; }
        `}</style>

        {/* Header with Logo */}
        {showLogo && logoUrl && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
            <img
              src={logoUrl} 
              alt="Company Logo" 
              style={{ height: '36px', maxWidth: '140px', objectFit: 'contain' }} 
            />
          </div>
        )}

        {/* Candidate Header */}
        <div 
          className="print-section"
          style={{ 
            borderBottom: '2px solid #1f2937', 
            paddingBottom: '10px', 
            marginBottom: '12px' 
          }}
        >
          <h1 style={{ 
            fontSize: '20pt', 
            fontWeight: 'bold', 
            color: '#111827', 
            margin: '0 0 6px 0' 
          }}>
            {displayName}
          </h1>
          
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', fontSize: '9pt', color: '#4B5563' }}>
            {!isAnonymized && candidate.city && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <MapPin style={{ width: '12px', height: '12px' }} />
                {candidate.city}
              </span>
            )}
            {candidate.availability_status && (
              <span style={{ 
                padding: '1px 6px', 
                backgroundColor: '#dcfce7', 
                borderRadius: '3px', 
                fontSize: '8pt',
                fontWeight: '500',
                color: '#166534'
              }}>
                {candidate.availability_status.replace(/_/g, ' ')}
              </span>
            )}
            {candidate.employment_status && (
              <span style={{ 
                padding: '1px 6px', 
                backgroundColor: '#e0e7ff', 
                borderRadius: '3px', 
                fontSize: '8pt',
                fontWeight: '500',
                textTransform: 'capitalize',
                color: '#3730a3'
              }}>
                {candidate.employment_status.replace(/_/g, ' ')}
              </span>
            )}
          </div>
        </div>

        {/* Two-column layout for contact and expectations */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '12px' }}>
          {/* Contact Information */}
          {!isAnonymized && (candidate.email_primary || candidate.phone_primary || candidate.linkedin_url) && (
            <div className="print-section">
              <h2 style={{ 
                fontSize: '11pt', 
                fontWeight: '600', 
                color: '#111827', 
                borderBottom: '1px solid #E5E7EB',
                paddingBottom: '3px',
                marginBottom: '6px'
              }}>
                Contact Information
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '9pt' }}>
                {candidate.email_primary && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Mail style={{ width: '12px', height: '12px', color: '#6B7280' }} />
                    {candidate.email_primary}
                  </div>
                )}
                {candidate.phone_primary && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Phone style={{ width: '12px', height: '12px', color: '#6B7280' }} />
                    {candidate.phone_primary}
                  </div>
                )}
                {candidate.linkedin_url && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Linkedin style={{ width: '12px', height: '12px', color: '#6B7280' }} />
                    <span style={{ wordBreak: 'break-all', fontSize: '8pt' }}>{candidate.linkedin_url}</span>
                  </div>
                )}
                {candidate.github_url && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Github style={{ width: '12px', height: '12px', color: '#6B7280' }} />
                    <span style={{ wordBreak: 'break-all', fontSize: '8pt' }}>{candidate.github_url}</span>
                  </div>
                )}
                {candidate.website_url && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Globe style={{ width: '12px', height: '12px', color: '#6B7280' }} />
                    <span style={{ wordBreak: 'break-all', fontSize: '8pt' }}>{candidate.website_url}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Salary & Availability */}
          {!isAnonymized && (formatSalary() || candidate.availability_date) && (
            <div className="print-section">
              <h2 style={{ 
                fontSize: '11pt', 
                fontWeight: '600', 
                color: '#111827', 
                borderBottom: '1px solid #E5E7EB',
                paddingBottom: '3px',
                marginBottom: '6px'
              }}>
                Expectations
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '9pt' }}>
                {formatSalary() && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <DollarSign style={{ width: '12px', height: '12px', color: '#6B7280' }} />
                    {formatSalary()}
                  </div>
                )}
                {candidate.availability_date && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Calendar style={{ width: '12px', height: '12px', color: '#6B7280' }} />
                    Available from {new Date(candidate.availability_date).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* AI Summary */}
        {showAISummary && (candidate.ai_skills_summary || candidate.ai_products_summary) && (
          <div 
            className="print-section avoid-break"
            style={{ 
              marginBottom: '12px', 
              backgroundColor: '#f8fafc', 
              padding: '10px',
              borderRadius: '4px',
              border: '1px solid #e2e8f0'
            }}
          >
            <h2 style={{ 
              fontSize: '11pt', 
              fontWeight: '600', 
              color: '#111827',
              marginBottom: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}>
              <Sparkles style={{ width: '14px', height: '14px', color: '#6B7280' }} />
              Professional Summary
            </h2>
            <div className="rich-text-print" style={{ fontSize: '9pt', color: '#374151' }}>
              {candidate.ai_skills_summary && renderRichText(candidate.ai_skills_summary)}
              {candidate.ai_products_summary && (
                <div style={{ marginTop: '4px' }}>
                  {renderRichText(candidate.ai_products_summary)}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Skills - Organized by Bucket */}
        {skillBuckets.length > 0 && (
          <div className="print-section" style={{ marginBottom: '14px' }}>
            <h2 style={{ 
              fontSize: '11pt', 
              fontWeight: '600', 
              color: '#111827', 
              borderBottom: '1px solid #E5E7EB',
              paddingBottom: '3px',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}>
              <FolderTree style={{ width: '14px', height: '14px', color: '#6B7280' }} />
              Technical Skills
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {skillBuckets.map((bucket) => (
                <div key={bucket.id} className="avoid-break" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <span style={{ 
                    fontSize: '8pt', 
                    fontWeight: '600', 
                    color: '#374151', 
                    minWidth: '100px',
                    flexShrink: 0
                  }}>
                    {bucket.name}:
                  </span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                    {bucket.skills.map((skill) => (
                      <span
                        key={skill.id}
                        style={{
                          padding: '1px 6px',
                          backgroundColor: '#f1f5f9',
                          borderRadius: '2px',
                          fontSize: '8pt',
                          color: '#1e293b'
                        }}
                      >
                        {skill.name}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Certifications */}
        {certifications.length > 0 && (
          <div className="print-section" style={{ marginBottom: '14px' }}>
            <h2 style={{ 
              fontSize: '11pt', 
              fontWeight: '600', 
              color: '#111827', 
              borderBottom: '1px solid #E5E7EB',
              paddingBottom: '3px',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}>
              <BadgeCheck style={{ width: '14px', height: '14px', color: '#6B7280' }} />
              Certifications
            </h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {certifications.map((cert: any) => (
                <span
                  key={cert.id}
                  style={{
                    padding: '3px 10px',
                    backgroundColor: '#fef3c7',
                    borderRadius: '3px',
                    fontSize: '9pt',
                    color: '#92400e',
                    fontWeight: '500'
                  }}
                >
                  {cert.skills?.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Experience */}
        {candidate.candidate_experience?.length > 0 && (
          <div className="print-section" style={{ marginBottom: '14px' }}>
            <h2 style={{ 
              fontSize: '11pt', 
              fontWeight: '600', 
              color: '#111827', 
              borderBottom: '1px solid #E5E7EB',
              paddingBottom: '3px',
              marginBottom: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}>
              <Briefcase style={{ width: '14px', height: '14px', color: '#6B7280' }} />
              Professional Experience
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {candidate.candidate_experience.map((exp: any) => (
                <div 
                  key={exp.id} 
                  className="avoid-break"
                  style={{ 
                    borderLeft: '2px solid #3b82f6', 
                    paddingLeft: '10px' 
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <h3 style={{ 
                        fontWeight: '600', 
                        color: '#111827', 
                        fontSize: '10pt',
                        margin: '0 0 1px 0'
                      }}>
                        {exp.job_title}
                      </h3>
                      <p style={{ 
                        fontSize: '9pt', 
                        color: '#374151', 
                        margin: '0',
                        fontWeight: '500'
                      }}>
                        {exp.companies?.name || exp.company_name || 'Unknown Company'}
                        {exp.companies?.primary_location && ` • ${exp.companies.primary_location}`}
                      </p>
                    </div>
                    <span style={{ fontSize: '8pt', color: '#6B7280', whiteSpace: 'nowrap' }}>
                      {formatDate(exp.start_date)} - {exp.is_current ? 'Present' : formatDate(exp.end_date)}
                    </span>
                  </div>
                  {exp.description && (
                    <div 
                      className="rich-text-print"
                      style={{ 
                        fontSize: '9pt', 
                        color: '#4B5563', 
                        marginTop: '4px',
                        lineHeight: '1.4'
                      }}
                    >
                      {renderRichText(exp.description)}
                    </div>
                  )}
                  {exp.products_worked?.length > 0 && (
                    <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                      <span style={{ fontSize: '8pt', color: '#6B7280', fontWeight: '500' }}>Products:</span>
                      {exp.products_worked.map((product: string, i: number) => (
                        <span 
                          key={i} 
                          style={{ 
                            fontSize: '8pt', 
                            backgroundColor: '#e0f2fe', 
                            padding: '1px 5px', 
                            borderRadius: '2px',
                            color: '#0369a1'
                          }}
                        >
                          {product}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Projects */}
        {candidate.candidate_projects?.length > 0 && (
          <div className="print-section" style={{ marginBottom: '14px' }}>
            <h2 style={{ 
              fontSize: '11pt', 
              fontWeight: '600', 
              color: '#111827', 
              borderBottom: '1px solid #E5E7EB',
              paddingBottom: '3px',
              marginBottom: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}>
              <FolderKanban style={{ width: '14px', height: '14px', color: '#6B7280' }} />
              Key Projects
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {candidate.candidate_projects.map((project: any) => (
                <div 
                  key={project.id} 
                  className="avoid-break"
                  style={{ 
                    borderLeft: '2px solid #10b981', 
                    paddingLeft: '10px' 
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <h3 style={{ 
                        fontWeight: '600', 
                        color: '#111827', 
                        fontSize: '10pt',
                        margin: '0 0 1px 0'
                      }}>
                        {project.name}
                      </h3>
                      {project.companies?.name && (
                        <p style={{ fontSize: '9pt', color: '#374151', margin: 0, fontWeight: '500' }}>
                          Client: {project.companies.name}
                        </p>
                      )}
                    </div>
                    {(project.start_date || project.end_date) && (
                      <span style={{ fontSize: '8pt', color: '#6B7280', whiteSpace: 'nowrap' }}>
                        {formatDate(project.start_date)} - {formatDate(project.end_date)}
                      </span>
                    )}
                  </div>
                  {project.description && (
                    <div 
                      className="rich-text-print"
                      style={{ 
                        fontSize: '9pt', 
                        color: '#4B5563', 
                        marginTop: '4px',
                        lineHeight: '1.4'
                      }}
                    >
                      {renderRichText(project.description)}
                    </div>
                  )}
                  {project.tech_stack?.length > 0 && (
                    <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                      <span style={{ fontSize: '8pt', color: '#6B7280', fontWeight: '500' }}>Tech:</span>
                      {project.tech_stack.map((tech: string, i: number) => (
                        <span 
                          key={i} 
                          style={{ 
                            fontSize: '8pt', 
                            backgroundColor: '#f0fdf4', 
                            padding: '1px 5px', 
                            borderRadius: '2px',
                            color: '#166534'
                          }}
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Education */}
        {candidate.candidate_education?.length > 0 && (
          <div className="print-section" style={{ marginBottom: '14px' }}>
            <h2 style={{ 
              fontSize: '11pt', 
              fontWeight: '600', 
              color: '#111827', 
              borderBottom: '1px solid #E5E7EB',
              paddingBottom: '3px',
              marginBottom: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}>
              <GraduationCap style={{ width: '14px', height: '14px', color: '#6B7280' }} />
              Education
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {candidate.candidate_education.map((edu: any) => (
                <div 
                  key={edu.id} 
                  className="avoid-break"
                  style={{ 
                    borderLeft: '2px solid #8b5cf6', 
                    paddingLeft: '10px' 
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <h3 style={{ 
                        fontWeight: '600', 
                        color: '#111827', 
                        fontSize: '10pt',
                        margin: '0 0 1px 0'
                      }}>
                        {edu.degree}
                        {edu.field_of_study && ` in ${edu.field_of_study}`}
                      </h3>
                      <p style={{ fontSize: '9pt', color: '#374151', margin: 0, fontWeight: '500' }}>
                        {isAnonymized ? 'Institution' : edu.institution_name}
                      </p>
                    </div>
                    <span style={{ fontSize: '8pt', color: '#6B7280', whiteSpace: 'nowrap' }}>
                      {formatDate(edu.start_date)} - {formatDate(edu.end_date)}
                    </span>
                  </div>
                  {edu.grade && (
                    <p style={{ fontSize: '8pt', color: '#6B7280', margin: '2px 0 0 0' }}>
                      Grade/CGPA: {edu.grade}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Awards & Achievements */}
        {awards.length > 0 && (
          <div className="print-section" style={{ marginBottom: '14px' }}>
            <h2 style={{ 
              fontSize: '11pt', 
              fontWeight: '600', 
              color: '#111827', 
              borderBottom: '1px solid #E5E7EB',
              paddingBottom: '3px',
              marginBottom: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}>
              <Award style={{ width: '14px', height: '14px', color: '#6B7280' }} />
              Awards & Achievements
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {awards.map((award: any) => (
                <div 
                  key={award.id} 
                  className="avoid-break"
                  style={{ 
                    borderLeft: '2px solid #f59e0b', 
                    paddingLeft: '10px' 
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <h3 style={{ 
                        fontWeight: '600', 
                        color: '#111827', 
                        fontSize: '10pt',
                        margin: '0 0 1px 0'
                      }}>
                        {award.title}
                      </h3>
                      {award.issuer && (
                        <p style={{ fontSize: '9pt', color: '#374151', margin: 0, fontWeight: '500' }}>
                          {award.issuer}
                        </p>
                      )}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {award.issue_date && (
                        <span style={{ fontSize: '8pt', color: '#6B7280', display: 'block' }}>
                          {formatDate(award.issue_date)}
                        </span>
                      )}
                      {award.award_type && (
                        <span style={{ 
                          fontSize: '7pt', 
                          padding: '1px 4px', 
                          backgroundColor: '#fef3c7', 
                          borderRadius: '2px',
                          color: '#92400e',
                          textTransform: 'capitalize'
                        }}>
                          {award.award_type}
                        </span>
                      )}
                    </div>
                  </div>
                  {award.description && (
                    <p style={{ fontSize: '8pt', color: '#6B7280', margin: '2px 0 0 0' }}>
                      {award.description}
                    </p>
                  )}
                  {award.credential_id && (
                    <p style={{ fontSize: '8pt', color: '#6B7280', margin: '2px 0 0 0' }}>
                      Credential ID: {award.credential_id}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AI Insights */}
        {candidate.candidate_ai_insights?.[0] && (
          <div 
            className="print-section avoid-break"
            style={{ 
              marginBottom: '14px', 
              backgroundColor: '#f8fafc', 
              padding: '10px',
              borderRadius: '4px',
              border: '1px solid #e2e8f0'
            }}
          >
            <h2 style={{ 
              fontSize: '11pt', 
              fontWeight: '600', 
              color: '#111827',
              marginBottom: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}>
              <Brain style={{ width: '14px', height: '14px', color: '#6B7280' }} />
              AI Assessment
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div>
                {candidate.candidate_ai_insights[0].summary && (
                  <div style={{ marginBottom: '6px' }}>
                    <span style={{ fontSize: '9pt', fontWeight: '600', color: '#374151' }}>Summary: </span>
                    <span style={{ fontSize: '9pt', color: '#4B5563' }}>
                      {candidate.candidate_ai_insights[0].summary}
                    </span>
                  </div>
                )}
                {candidate.candidate_ai_insights[0].strengths && (
                  <div>
                    <span style={{ fontSize: '9pt', fontWeight: '600', color: '#374151' }}>Strengths: </span>
                    <span style={{ fontSize: '9pt', color: '#4B5563' }}>
                      {candidate.candidate_ai_insights[0].strengths}
                    </span>
                  </div>
                )}
              </div>
              <div>
                {candidate.candidate_ai_insights[0].weaknesses && (
                  <div style={{ marginBottom: '6px' }}>
                    <span style={{ fontSize: '9pt', fontWeight: '600', color: '#374151' }}>Development Areas: </span>
                    <span style={{ fontSize: '9pt', color: '#4B5563' }}>
                      {candidate.candidate_ai_insights[0].weaknesses}
                    </span>
                  </div>
                )}
                {candidate.candidate_ai_insights[0].role_fit_score && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '9pt', fontWeight: '600', color: '#374151' }}>Role Fit: </span>
                    <span style={{ 
                      fontSize: '14pt', 
                      fontWeight: 'bold',
                      color: candidate.candidate_ai_insights[0].role_fit_score >= 7 ? '#059669' : 
                             candidate.candidate_ai_insights[0].role_fit_score >= 5 ? '#d97706' : '#dc2626'
                    }}>
                      {candidate.candidate_ai_insights[0].role_fit_score}/10
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ 
          marginTop: '16px', 
          paddingTop: '8px', 
          borderTop: '1px solid #E5E7EB', 
          textAlign: 'center', 
          fontSize: '7pt', 
          color: '#9CA3AF' 
        }}>
          <p style={{ margin: 0 }}>
            Generated on {new Date().toLocaleDateString()} • Confidential Document
          </p>
        </div>
      </div>
    );
  }
);

PrintCandidateProfile.displayName = 'PrintCandidateProfile';

export default PrintCandidateProfile;